import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert
} from 'react-bootstrap';
import { FaRupeeSign, FaCreditCard, FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import StudentService from '../../../services/studentService';

const AddPaymentModal = ({ show, onHide, studentId, studentName, courses, onSuccess }) => {
  const [formData, setFormData] = useState({
    course_id: '',
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0], // NEW: Default to today's date
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Debug log
  useEffect(() => {
    if (show) {
      console.log('AddPaymentModal opened for student:', studentId);
      console.log('Courses with due:', courses.filter(c => c.due_amount > 0));
      // Reset payment date to today when modal opens
      setFormData(prev => ({
        ...prev,
        payment_date: new Date().toISOString().split('T')[0]
      }));
    }
  }, [show, studentId, courses]);

  // Get courses with due amount
  const coursesWithDue = courses.filter(c => {
    const due = parseFloat(c.due_amount || c.total_fees - c.paid_amount);
    return due > 0;
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.course_id) {
      newErrors.course_id = 'Please select a course';
    }
    
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else {
      // Check if amount exceeds due
      const selectedCourse = courses.find(c => c.course_id === parseInt(formData.course_id));
      if (selectedCourse) {
        const dueAmount = parseFloat(selectedCourse.due_amount || selectedCourse.total_fees - selectedCourse.paid_amount);
        if (parseFloat(formData.amount) > dueAmount) {
          newErrors.amount = `Amount cannot exceed due amount (${formatCurrency(dueAmount)})`;
        }
      }
    }

    // NEW: Validate payment date
    if (!formData.payment_date) {
      newErrors.payment_date = 'Payment date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      console.log('Submitting payment:', {
        studentId,
        course_id: parseInt(formData.course_id),
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        remarks: formData.remarks
      });

      const response = await StudentService.addPayment(studentId, {
        course_id: parseInt(formData.course_id),
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        remarks: formData.remarks
      });
      
      console.log('Payment response:', response);
      
      toast.success(`Payment of ${formatCurrency(formData.amount)} added successfully`);
      onSuccess();
      
      // Reset form
      setFormData({
        course_id: '',
        amount: '',
        payment_method: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        remarks: ''
      });
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to add payment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCreditCard className="me-2 text-success" />
          Add Payment for {studentName}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {coursesWithDue.length === 0 ? (
            <Alert variant="info">
              No courses with due amount found for this student.
            </Alert>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select Course <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleChange}
                  isInvalid={!!errors.course_id}
                  disabled={loading}
                >
                  <option value="">-- Select a course --</option>
                  {coursesWithDue.map(course => {
                    const dueAmount = parseFloat(course.due_amount || course.total_fees - course.paid_amount);
                    return (
                      <option key={course.course_id || course.id} value={course.course_id || course.id}>
                        {course.course_name || course.name} - Due: {formatCurrency(dueAmount)}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.course_id}
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                    <div className="d-flex align-items-center">
                      <div className="bg-light px-3 py-2 border rounded-start">
                        <FaRupeeSign />
                      </div>
                      <Form.Control
                        type="number"
                        name="amount"
                        placeholder="Enter amount"
                        value={formData.amount}
                        onChange={handleChange}
                        isInvalid={!!errors.amount}
                        disabled={loading}
                        className="rounded-start-0"
                        min="1"
                        step="1"
                      />
                    </div>
                    <Form.Control.Feedback type="invalid">
                      {errors.amount}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Method <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="online">Online Transfer</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* NEW: Payment Date Field */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Date <span className="text-danger">*</span></Form.Label>
                    <div className="d-flex align-items-center">
                      <div className="bg-light px-3 py-2 border rounded-start">
                        <FaCalendarAlt />
                      </div>
                      <Form.Control
                        type="date"
                        name="payment_date"
                        value={formData.payment_date}
                        onChange={handleChange}
                        isInvalid={!!errors.payment_date}
                        disabled={loading}
                        className="rounded-start-0"
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Select the date when payment was actually made
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.payment_date}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Remarks (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  name="remarks"
                  rows={2}
                  placeholder="Add any remarks about this payment"
                  value={formData.remarks}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Form.Group>

              {formData.course_id && formData.amount && (
                <Alert variant="info">
                  <small>
                    <strong>Note:</strong> This payment will be recorded as received by Admin.
                    Receipt will be generated automatically with the selected date.
                  </small>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={loading || coursesWithDue.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Processing...
              </>
            ) : (
              'Add Payment'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
 
export default AddPaymentModal;